import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';

import ApiContext from 'contexts/apiContext';
import ErrorContext from 'contexts/errorContext';
import { APIError, FieldErrors, FieldsAPIError } from 'errors';
import { Store, StoreUpdate } from 'types/store';
import { makeStyles } from 'tss-react/mui';
import MuiPhoneNumber from 'material-ui-phone-number';
import { capitalizeFirstLetter } from 'utils';

type StoreConfig = {
  payment_methods: { code: string; label: string }[];
};

const useStyles = makeStyles()((theme) => ({
  field: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  paymentMethodsContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  paymentMethodItem: {
    marginBottom: theme.spacing(0.5),
  },
}));

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  store: Store;
};

export default function StoreEditDialog({ isOpen, onClose, onSuccess, store }: Props) {
  const { api } = useContext(ApiContext);
  const { setError } = useContext(ErrorContext);
  const { classes } = useStyles();

  const [storeData, setStoreData] = useState<StoreUpdate>({});
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState<StoreConfig | null>(null);

  const handleError = useCallback(
    (err?: APIError) => {
      if (err) {
        if (err instanceof FieldsAPIError) setFieldErrors(err.fieldErrors);
        else setError(err);
      } else {
        setFieldErrors(undefined);
        setError(false);
      }
    },
    [setError, setFieldErrors],
  );

  useEffect(() => {
    if (isOpen && store) {
      setStoreData({
        description: store.description,
        accepts_in_store_payment: store.accepts_in_store_payment,
        accepted_payment_methods: store.accepted_payment_methods,
        allows_byobox: store.allows_byobox,
        contact_phone_number: store.contact_phone_number,
      });
      setConfig(null);
      api.stores
        .getConfig(store.id)
        .then((response: any) => {
          setConfig(response.data);
        })
        .catch((err: any) => {
          handleError(err);
        });
    }
  }, [isOpen, store, api.stores, handleError]);

  const handleSubmit = (event: any) => {
    event.preventDefault();
    handleError(undefined);
    setLoading(true);

    let dataToSend = { ...storeData };

    // If the store doesn't have in-store payments, don't allow enabling it
    if (!store.accepts_in_store_payment) {
      delete dataToSend.accepts_in_store_payment;
      dataToSend.accepted_payment_methods = [];
    }

    // If in-store payments are being disabled, clear accepted_payment_methods
    if (dataToSend.accepts_in_store_payment === false) {
      dataToSend.accepted_payment_methods = [];
    }

    api.stores
      .update(store.id, dataToSend)
      .then(() => handleClose(true))
      .catch(handleError)
      .finally(() => setLoading(false));
  };

  const handleClose = (success = false) => {
    handleError(undefined);
    success ? onSuccess() : onClose();
  };

  const handleChange = (event: any) => {
    const { name, value, type, checked } = event.target;
    setStoreData((prevState) => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePaymentMethodChange = (paymentMethod: string, checked: boolean) => {
    setStoreData((prevState) => {
      const currentMethods = prevState.accepted_payment_methods || [];
      const updatedMethods = checked ? [...currentMethods, paymentMethod] : currentMethods.filter((method) => method !== paymentMethod);

      return {
        ...prevState,
        accepted_payment_methods: updatedMethods,
      };
    });
  };

  const getFieldError = (field: string) => fieldErrors && fieldErrors[field];

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Editar información de la tienda</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <FormControl className={classes.field} fullWidth>
            <TextField
              margin='dense'
              id='description'
              name='description'
              label='Descripción'
              value={storeData.description || ''}
              error={!!getFieldError('description')}
              helperText={getFieldError('description')}
              onChange={handleChange}
              type='text'
              multiline
              rows={3}
            />
          </FormControl>

          <FormControl className={classes.field} fullWidth>
            <MuiPhoneNumber
              defaultCountry={store.country.country_code ? store.country.country_code.toLowerCase() : 'uy'}
              onlyCountries={['uy', 'ar', 'co']}
              value={storeData.contact_phone_number || ''}
              onChange={(value) => handleChange({ target: { name: 'contact_phone_number', value } })}
              label='Teléfono de contacto'
              fullWidth
              required
              error={!!getFieldError('contact_phone_number')}
              helperText={getFieldError('contact_phone_number')}
            />
          </FormControl>

          {store.accepts_in_store_payment && (
            <>
              <FormControl className={classes.field} fullWidth>
                <Typography variant='subtitle1' gutterBottom>
                  Pago en tienda
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      name='accepts_in_store_payment'
                      checked={storeData.accepts_in_store_payment || false}
                      onChange={handleChange}
                      disabled={!store.has_in_app_payments}
                    />
                  }
                  label='Acepta pago en tienda'
                />
                {getFieldError('accepts_in_store_payment') && (
                  <FormHelperText error>{getFieldError('accepts_in_store_payment')}</FormHelperText>
                )}
              </FormControl>

              {config && config.payment_methods.length > 0 && (
                <FormControl className={classes.field} fullWidth disabled={!storeData.accepts_in_store_payment}>
                  <Typography variant='subtitle2' gutterBottom>
                    Medios de pago en tienda
                  </Typography>
                  <Box className={classes.paymentMethodsContainer} ml={1}>
                    {config.payment_methods.map((method) => (
                      <FormControlLabel
                        key={method.code}
                        control={
                          <Checkbox
                            checked={storeData.accepts_in_store_payment && (storeData.accepted_payment_methods || []).includes(method.code)}
                            onChange={(e) => handlePaymentMethodChange(method.code, e.target.checked)}
                            disabled={!storeData.accepts_in_store_payment}
                          />
                        }
                        label={capitalizeFirstLetter(method.label)}
                        className={classes.paymentMethodItem}
                      />
                    ))}
                  </Box>
                  {storeData.accepts_in_store_payment && storeData.accepted_payment_methods?.length === 0 && (
                    <FormHelperText error>Debe seleccionar al menos un medio de pago</FormHelperText>
                  )}
                </FormControl>
              )}
            </>
          )}

          <FormControl className={classes.field} fullWidth>
            <Typography variant='subtitle1' gutterBottom>
              Retiro
            </Typography>
            <FormControlLabel
              control={<Checkbox name='allows_byobox' checked={storeData.allows_byobox || false} onChange={handleChange} />}
              label='Permite retiro con recipiente propio'
            />
            {getFieldError('allows_byobox') && <FormHelperText error>{getFieldError('allows_byobox')}</FormHelperText>}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type='submit'
            variant='contained'
            color='primary'
            disabled={loading || (storeData.accepts_in_store_payment && storeData.accepted_payment_methods?.length === 0)}
          >
            Guardar
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
